// frontend/src/components/EventLocation.js
import { useTranslation } from 'react-i18next';
import { MapPin, Navigation, Phone, Home } from 'lucide-react';

const EventLocation = () => {
  const { t } = useTranslation();
  
  const mapUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6172.182493911646!2d23.033787144268736!3d39.331518136538165!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14a7137d420744e3%3A0xe8a718977f3ac5bc!2sKato%20Lechonia%20373%2000%2C%20Greece!5e0!3m2!1sen!2sde!4v1760862629430!5m2!1sen!2sde";
  const mapDirectLink = "https://maps.google.com/?q=Kato+Lechonia+373+00+Greece";
  
  const airbnbWishlistUrl = "https://www.airbnb.de/wishlists/invite/a3be124a-4b1a-4ddf-9d85-ad25101e1647?s=67&unique_share_id=5619f856-c74c-4355-bf2e-3b75e860508b"; // Replace with your actual wishlist URL

  const venueDetails = {
    name: `${t('nameOfVenue')}`,
    address: `${t('location')}`,
    phone: "+49 179 6772046",
    phone2: "+30 6948002940",
    googleMapsLink: mapDirectLink
  };

  return (
    <section id="location" className="py-20 bg-gradient-to-b from-stone-100 to-amber-50">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-gray-800">
          {t('eventLocation')}
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          {t('eventLocationDesc')}
        </p>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Map */}
          <div className="rounded-2xl overflow-hidden shadow-2xl h-96 md:h-full">
            <iframe
              src={mapUrl}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Event Location Map"
            ></iframe>
          </div>

          {/* Venue Details */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-gray-50 to-gray-80 rounded-2xl p-8 border border-gray-200 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">{venueDetails.name}</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-gray-100 p-3 rounded-lg flex-shrink-0">
                    <MapPin className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">{t('address')}</h4>
                    <p className="text-gray-600">{venueDetails.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-gray-100 p-3 rounded-lg flex-shrink-0">
                    <Phone className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">{t('phone')}</h4>
                    <div className="space-y-1">
                      <a href={`tel:${venueDetails.phone}`} className="block text-gray-600 hover:underline">
                        {venueDetails.phone} 🇩🇪
                      </a>
                      <a href={`tel:${venueDetails.phone2}`} className="block text-gray-600 hover:underline">
                        {venueDetails.phone2} 🇬🇷
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-gray-100 p-3 rounded-lg flex-shrink-0">
                    <Navigation className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">{t('directions')}</h4>
                    <a
                      href={venueDetails.googleMapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-600 hover:underline"
                    >
                      {t('getDirections')}
                    </a>
                  </div>
                </div>
              </div>

              <a
                href={venueDetails.googleMapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 block w-full bg-gradient-to-r from-gray-700 to-gray-700 text-white font-semibold py-3 px-6 rounded-xl hover:from-gray-900 hover:to-gray-900 transform transition-all duration-200 text-center"
              >
                {t('openInMaps')} →
              </a>
            </div>

            {/* Accommodation Info with Airbnb Link */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-80 rounded-2xl p-8 border border-gray-200 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-gray-300 p-3 rounded-lg">
                  <Home className="w-6 h-6 text-gray-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-800">{t('accommodation.title')}</h4>
              </div>
              <p className="text-gray-600 mb-4">
                {t('accommodation.description')}
              </p>
              <a
                href={airbnbWishlistUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-gray-700 to-gray-700 text-white font-semibold py-3 px-6 rounded-xl hover:from-gray-900 hover:to-gray-900 transform transition-all duration-200 text-center"
              >
                🏠 {t('accommodation.viewAirbnbs')}
              </a>
              <p className="text-sm text-gray-500 mt-3 text-center">
                {t('accommodation.bookEarly')}
              </p>
            </div>

            {/* Parking Info */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg border border-gray-200 shadow-lg">
              <h4 className="font-bold text-yellow-800 mb-2">🅿️ {t('parkingInfo')}</h4>
              <p className="text-yellow-700 text-sm">{t('parkingInfoDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventLocation;